import { useState } from "react";
import { Link, useLocation, useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { useClinicAuth } from "../context/ClinicAuthContext";

function formatErr(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function ClinicLoginPage({ mode = "login" }) {
  const { login, register, clinic, loading } = useClinicAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [clinicName, setClinicName] = useState("");
  const [contactName, setContactName] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const nav = useNavigate();
  const loc = useLocation();

  if (loading) return <div className="min-h-[50vh] flex items-center justify-center font-serif text-2xl">Loading…</div>;
  if (clinic) return <Navigate to="/clinics/dashboard" replace />;

  const isRegister = mode === "register";
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      if (isRegister) {
        await register({ email, password, clinic_name: clinicName, contact_name: contactName, phone });
        toast.success("Welcome to ALTA for Clinics.");
      } else {
        await login(email, password);
        toast.success("Welcome back.");
      }
      nav("/clinics/dashboard");
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[80vh] grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src="/generated/hero_mommy.png" alt="ALTA for clinics" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#2A1F1F]/70 via-transparent to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-[#FBF6F1]">
          <div className="eyebrow" style={{color:"#E5C1AF"}}>For Clinics</div>
          <p className="font-serif text-3xl mt-4 leading-tight max-w-md">
            Give your patients a recovery experience that reflects your standard.
          </p>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <div className="eyebrow">{isRegister ? "Apply for Access" : "Clinic Sign In"}</div>
          <h1 className="font-serif text-3xl sm:text-4xl mt-4 leading-tight">
            {isRegister ? "Bring ALTA to your practice." : "Welcome back."}
          </h1>
          <p className="text-[#6B5757] mt-4 text-sm">
            {isRegister
              ? "Create your clinic account. We'll verify your practice within one business day."
              : "Access your dashboard to place patient-specific orders."}
          </p>

          <form onSubmit={submit} className="mt-10 space-y-4">
            {isRegister && (
              <>
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[#6B5757]">Clinic Name</span>
                  <input
                    data-testid="clinic-register-clinic-name"
                    required minLength={2}
                    value={clinicName} onChange={(e) => setClinicName(e.target.value)}
                    className="mt-1 w-full px-4 py-3 text-sm bg-white border border-[#E5CDBB] rounded-md focus:outline-none focus:border-[#2A1F1F]"
                  />
                </label>
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[#6B5757]">Contact Name (Surgeon)</span>
                  <input
                    data-testid="clinic-register-contact-name"
                    required minLength={2}
                    value={contactName} onChange={(e) => setContactName(e.target.value)}
                    className="mt-1 w-full px-4 py-3 text-sm bg-white border border-[#E5CDBB] rounded-md focus:outline-none focus:border-[#2A1F1F]"
                  />
                </label>
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[#6B5757]">Phone</span>
                  <input
                    value={phone} onChange={(e) => setPhone(e.target.value)}
                    className="mt-1 w-full px-4 py-3 text-sm bg-white border border-[#E5CDBB] rounded-md focus:outline-none focus:border-[#2A1F1F]"
                  />
                </label>
              </>
            )}
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-widest text-[#6B5757]">Email</span>
              <input
                data-testid="clinic-auth-email"
                type="email" required
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full px-4 py-3 text-sm bg-white border border-[#E5CDBB] rounded-md focus:outline-none focus:border-[#2A1F1F]"
              />
            </label>
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-widest text-[#6B5757]">Password{isRegister && " · min 8 chars"}</span>
              <input
                data-testid="clinic-auth-password"
                type="password" required minLength={isRegister ? 8 : 1}
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full px-4 py-3 text-sm bg-white border border-[#E5CDBB] rounded-md focus:outline-none focus:border-[#2A1F1F]"
              />
            </label>
            {err && <div data-testid="clinic-auth-error" className="text-sm text-[#B85C56]">{err}</div>}
            <button
              data-testid="clinic-auth-submit"
              type="submit" disabled={busy}
              className="btn-primary w-full disabled:opacity-60"
            >
              {busy ? "Please wait…" : isRegister ? "Create Account" : "Sign In"}
            </button>
          </form>

          <div className="mt-6 text-sm text-[#6B5757]">
            {isRegister ? (
              <>Already have an account? <Link to="/clinics/login" className="text-[#B85C56] hover:underline">Sign in</Link></>
            ) : (
              <>New to ALTA for Clinics? <Link to="/clinics/register" className="text-[#B85C56] hover:underline">Apply for access</Link></>
            )}
          </div>

          {!isRegister && (
            <div className="mt-8 border border-dashed border-[#E5CDBB] rounded-md p-4 text-xs text-[#6B5757]">
              <div className="font-mono uppercase tracking-widest text-[10px] mb-2 text-[#B85C56]">Demo account</div>
              dr.smith@alta-clinic-demo.com<br />
              AltaDemo2026!
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
